import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import AIService from '../../services/aiService';
import PageHeader from '../common/PageHeader';
import { Plus, Trash2, ChevronDown, ChevronUp, Wand2, Save, AlertCircle, CheckCircle, Clock, FileText, Upload, Download, ExternalLink, X, Settings2 } from 'lucide-react';

const PHASES = [
  {
    id: 'identificationOfNeed',
    title: 'Identification of Need',
    subtitle: 'Project Conceptualization',
    icon: '📋',
    color: 'bg-blue-50 border-blue-200',
    headerColor: 'text-blue-700',
    fields: [
      { key: 'problemStatement', label: 'Problem Statement', type: 'textarea' },
      { key: 'proposedSolution', label: 'Proposed Solution', type: 'textarea' },
      { key: 'originSource', label: 'Origin Source', type: 'text', hint: 'e.g., Departmental need, Citizen complaint' },
      { key: 'indicativeCost', label: 'Indicative Cost (₹)', type: 'number' },
      { key: 'expectedBenefits', label: 'Expected Benefits', type: 'textarea' }
    ]
  },
  {
    id: 'budgetInclusion',
    title: 'Budget Inclusion',
    subtitle: 'Plan Approval',
    icon: '💰',
    color: 'bg-green-50 border-green-200',
    headerColor: 'text-green-700',
    fields: [
      { key: 'budgetHead', label: 'Budget Head', type: 'text' },
      { key: 'capitalWorksplanAlignment', label: 'Capital Works Plan Alignment', type: 'textarea' },
      { key: 'cdpAlignment', label: 'City Development Plan Aligned', type: 'checkbox' },
      { key: 'reappropriationRequired', label: 'Reappropriation Required', type: 'checkbox' },
      { key: 'budgetAllocation', label: 'Budget Allocation (₹)', type: 'number' },
      { key: 'budgetApprovalDate', label: 'Approval Date', type: 'date' }
    ]
  },
  {
    id: 'adminApproval',
    title: 'Administrative Approval',
    subtitle: 'AA - Official Sanction',
    icon: '✅',
    color: 'bg-purple-50 border-purple-200',
    headerColor: 'text-purple-700',
    fields: [
      { key: 'projectNecessity', label: 'Project Necessity', type: 'textarea' },
      { key: 'fundAvailability', label: 'Funds Available', type: 'checkbox' },
      { key: 'administrativeFeasibility', label: 'Administrative Feasibility', type: 'textarea' },
      { key: 'approvalAuthority', label: 'Approval Authority', type: 'text' },
      { key: 'aaApprovalDate', label: 'AA Date', type: 'date' },
      { key: 'aaOrderReference', label: 'AA Order Reference', type: 'text' }
    ]
  },
  {
    id: 'consultantAppointment',
    title: 'Consultant Appointment',
    subtitle: 'If Applicable',
    icon: '👤',
    color: 'bg-indigo-50 border-indigo-200',
    headerColor: 'text-indigo-700',
    fields: [
      { key: 'consultantRequired', label: 'Consultant Required', type: 'checkbox' },
      { key: 'eoiIssued', label: 'EOI Issued', type: 'checkbox' },
      { key: 'rfqIssued', label: 'RFQ Issued', type: 'checkbox' },
      { key: 'selectedConsultant', label: 'Selected Consultant', type: 'text' },
      { key: 'consultantContractDate', label: 'Contract Date', type: 'date' },
      { key: 'consultantResponsibilities', label: 'Responsibilities', type: 'list' }
    ]
  },
  {
    id: 'dprPreparation',
    title: 'DPR Preparation',
    subtitle: 'Detailed Project Report',
    icon: '📄',
    color: 'bg-red-50 border-red-200',
    headerColor: 'text-red-700',
    fields: [
      { key: 'dprScope', label: 'DPR Scope', type: 'textarea' },
      { key: 'technicalArchitecture', label: 'Technical Architecture', type: 'textarea' },
      { key: 'boqEstimate', label: 'BOQ Estimate (₹)', type: 'number' },
      { key: 'implementationTimeline', label: 'Implementation Timeline', type: 'text' },
      { key: 'riskAssessment', label: 'Risk Assessment', type: 'textarea' },
      { key: 'oAndMModel', label: 'Operations & Maintenance Model', type: 'textarea' },
      { key: 'dprDocument', label: 'DPR Document', type: 'text' },
      { key: 'dprPreparedBy', label: 'DPR Prepared By', type: 'text' }
    ]
  },
  {
    id: 'technicalSanction',
    title: 'Technical Sanction',
    subtitle: 'TS - Engineering Approval',
    icon: '⚙️',
    color: 'bg-indigo-50 border-indigo-200',
    headerColor: 'text-indigo-700',
    fields: [
      { key: 'chiefEngineerReview', label: 'Chief Engineer Review', type: 'textarea' },
      { key: 'complianceWithStandards', label: 'Standards Compliance Verified', type: 'checkbox' },
      { key: 'standardsReference', label: 'Standards Reference', type: 'text' },
      { key: 'costEstimationVerified', label: 'Cost Estimation Verified', type: 'checkbox' },
      { key: 'technicalSanctionDate', label: 'TS Date', type: 'date' },
      { key: 'tsOrderReference', label: 'TS Order Reference', type: 'text' }
    ]
  },
  {
    id: 'clearances',
    title: 'Statutory Clearances',
    subtitle: 'Environmental & Regulatory',
    icon: '🔒',
    color: 'bg-cyan-50 border-cyan-200',
    headerColor: 'text-cyan-700',
    fields: [
      { key: 'environmentClearance', label: 'Environment Clearance', type: 'checkbox' },
      { key: 'environmentClearanceDate', label: 'Clearance Date', type: 'date' },
      { key: 'trafficNoc', label: 'Traffic NOC', type: 'checkbox' },
      { key: 'townPlanningApproval', label: 'Town Planning Approval', type: 'checkbox' },
      { key: 'stateDepartmentConcurrence', label: 'State Department Concurrence', type: 'checkbox' },
      { key: 'otherClearances', label: 'Other Clearances', type: 'list' },
      { key: 'allClearancesObtained', label: 'All Clearances Obtained', type: 'checkbox' },
      { key: 'clearanceNotes', label: 'Notes', type: 'textarea' }
    ]
  },
  {
    id: 'procurementApproval',
    title: 'Procurement Approval',
    subtitle: 'Final Cost & Bidding Strategy',
    icon: '📊',
    color: 'bg-lime-50 border-lime-200',
    headerColor: 'text-lime-700',
    fields: [
      { key: 'procurementStrategy', label: 'Procurement Strategy', type: 'text' },
      { key: 'biddingStage', label: 'Bidding Stage', type: 'text' },
      { key: 'approvalAuthority', label: 'Approval Authority', type: 'text' },
      { key: 'procurementApprovalDate', label: 'Approval Date', type: 'date' },
      { key: 'procurementOrderReference', label: 'Procurement Order Reference', type: 'text' },
      { key: 'delegatedLimits', label: 'Delegated Limits', type: 'text' }
    ]
  },
  {
    id: 'rfpDrafting',
    title: 'RFP Drafting',
    subtitle: 'Tender Documents Preparation',
    icon: '📝',
    color: 'bg-pink-50 border-pink-200',
    headerColor: 'text-pink-700',
    fields: [
      { key: 'eligibilityCriteria', label: 'Eligibility Criteria', type: 'textarea' },
      { key: 'technicalEvaluationFramework', label: 'Technical Evaluation Framework', type: 'textarea' },
      { key: 'financialEvaluationMethod', label: 'Financial Evaluation Method', type: 'textarea' },
      { key: 'slaAndPenalties', label: 'SLA & Penalties', type: 'textarea' },
      { key: 'contractConditions', label: 'Contract Conditions', type: 'textarea' },
      { key: 'rfpCompliance', label: 'RFP Compliance Standards', type: 'text' },
      { key: 'rfpReady', label: 'RFP Ready for Publication', type: 'checkbox' }
    ]
  },
  {
    id: 'legalScrutiny',
    title: 'Legal Scrutiny',
    subtitle: 'Pre-RFP Vetting',
    icon: '⚖️',
    color: 'bg-amber-50 border-amber-200',
    headerColor: 'text-amber-700',
    fields: [
      { key: 'legalReviewStatus', label: 'Legal Review Status', type: 'text' },
      { key: 'legalReviewDate', label: 'Legal Review Date', type: 'date' },
      { key: 'vigilanceCheckCompleted', label: 'Vigilance Check Completed', type: 'checkbox' },
      { key: 'vigilanceCheckDate', label: 'Vigilance Check Date', type: 'date' },
      { key: 'complianceVerified', label: 'Compliance Verified', type: 'checkbox' },
      { key: 'legalApprovalDate', label: 'Legal Approval Date', type: 'date' },
      { key: 'rfpApproved', label: 'RFP Approved for Publication', type: 'checkbox' },
      { key: 'legalApprovalReference', label: 'Legal Approval Reference', type: 'text' }
    ]
  }
];

const sampleData = {
  identificationOfNeed: {
    problemStatement:
      "MSMEs face challenges in managing inventory, accounting, procurement, and sales across multiple disconnected systems, leading to inefficiencies and data inconsistencies.",
    proposedSolution:
      "Develop a cloud-based Smart ERP platform integrating inventory, finance, CRM, procurement, and reporting modules.",
    originSource: "Departmental Digital Transformation Initiative",
    indicativeCost: 8500000,
    expectedBenefits:
      "Improved operational efficiency, centralized data management, reduced manual effort, and real-time business insights."
  },

  budgetInclusion: {
    budgetHead: "Digital Infrastructure & Enterprise Applications",
    capitalWorksplanAlignment:
      "Aligned with annual digital modernization and MSME support initiatives.",
    cdpAlignment: true,
    reappropriationRequired: false,
    budgetAllocation: 9000000,
    budgetApprovalDate: "2025-01-15"
  },

  adminApproval: {
    projectNecessity:
      "Need to streamline operations and improve decision-making capabilities for MSMEs through digital transformation.",
    fundAvailability: true,
    administrativeFeasibility:
      "Resources, budget, and implementation teams are available for project execution.",
    approvalAuthority: "Municipal Commissioner",
    aaApprovalDate: "2025-02-01",
    aaOrderReference: "AA/ERP/2025/014"
  },

  consultantAppointment: {
    consultantRequired: true,
    eoiIssued: true,
    rfqIssued: true,
    selectedConsultant: "TechNova Consulting Pvt Ltd",
    consultantContractDate: "2025-02-20",
    consultantResponsibilities: [
      "Requirement Gathering",
      "System Architecture Design",
      "Project Management",
      "Quality Assurance"
    ]
  },

  dprPreparation: {
    dprScope:
      "Design, development, deployment, training, and maintenance of Smart ERP platform.",
    technicalArchitecture:
      "MERN Stack application hosted on cloud infrastructure with microservice-based backend architecture.",
    boqEstimate: 8750000,
    implementationTimeline: "12 months",
    riskAssessment:
      "User adoption risk, integration challenges, data migration issues, and cybersecurity concerns.",
    oAndMModel:
      "Annual AMC with dedicated support team and cloud infrastructure maintenance.",
    dprDocument: "DPR_Smart_ERP_v1.2.pdf",
    dprPreparedBy: "TechNova Consulting Pvt Ltd"
  },

  technicalSanction: {
    chiefEngineerReview:
      "Technical design reviewed and found compliant with enterprise architecture standards.",
    complianceWithStandards: true,
    standardsReference: "NIC Cloud Guidelines, ISO 27001, OWASP",
    costEstimationVerified: true,
    technicalSanctionDate: "2025-03-10",
    tsOrderReference: "TS/ERP/2025/027"
  },

  clearances: {
    environmentClearance: false,
    environmentClearanceDate: "",
    trafficNoc: false,
    townPlanningApproval: false,
    stateDepartmentConcurrence: true,
    otherClearances: [
      "IT Department Approval",
      "Data Security Compliance Review"
    ],
    allClearancesObtained: true,
    clearanceNotes:
      "Software project; environmental and traffic clearances not applicable."
  },

  procurementApproval: {
    procurementStrategy: "Open Tender",
    biddingStage: "Two-stage",
    approvalAuthority: "Procurement Committee",
    procurementApprovalDate: "2025-03-25",
    procurementOrderReference: "PA/ERP/2025/041",
    delegatedLimits: "Up to ₹1 Crore approved under committee authority"
  },

  rfpDrafting: {
    eligibilityCriteria:
      "Minimum 5 years experience in ERP implementation and 3 similar government projects completed.",
    technicalEvaluationFramework:
      "70% technical score based on experience, methodology, team strength, and product capabilities.",
    financialEvaluationMethod:
      "QCBS (Quality and Cost Based Selection) with 70:30 weightage.",
    slaAndPenalties:
      "99.5% uptime, response time within 4 hours, penalties for SLA breaches.",
    contractConditions:
      "12-month implementation period with 3 years support and maintenance.",
    rfpCompliance:
      "MTPP Act, Government e-Procurement Guidelines",
    rfpReady: true
  },

  legalScrutiny: {
    legalReviewStatus: "Completed",
    legalReviewDate: "2025-04-05",
    vigilanceCheckCompleted: true,
    vigilanceCheckDate: "2025-04-07",
    complianceVerified: true,
    legalApprovalDate: "2025-04-10",
    rfpApproved: true,
    legalApprovalReference: "LEGAL/ERP/2025/019"
  }
};

const DPR_UPLOAD_MESSAGES = [
  'Reading document...',
  'Analyzing first section...',
  'Analyzing second section...',
  'Merging extracted data...',
  'Mapping to form fields...',
  'Almost done...',
];

export default function PreRftProjectDetails() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [searchParams] = useSearchParams();

const initialPhase =
  searchParams.get('phase') || 'identificationOfNeed';

const [activePhase, setActivePhase] = useState(initialPhase);
  const [expandedPhase, setExpandedPhase] = useState('identificationOfNeed');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fillingPhaseId, setFillingPhaseId] = useState(null); // Tracks which phase is being filled by AI
  const [workflowMode, setWorkflowMode] = useState('manual');
  
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ title: '', description: '' });
  
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [dprUploading, setDprUploading] = useState(false);
  const [dprFileName, setDprFileName] = useState('');
  const dprInputRef = useRef(null);

  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  const autoSaveTimerRef = useRef(null);
  const activeProjectRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const res = await api.get('/sourcing/pre-rft/projects');
      console.log('PROJECTS:', res.data.projects);
console.log('FIRST PROJECT:', res.data.projects?.[0]);
      if (res.data) {
        setProjects(res.data.projects || []);
    

if (projectId) {
  setActiveProjectId(projectId);
} else if (res.data.activeProjectId) {
  setActiveProjectId(res.data.activeProjectId);
} else if (res.data.projects?.length > 0) {
  setActiveProjectId(res.data.projects[0].id);
}
      }
    } catch (err) {
      showToast('Failed to load project data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast, projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getActiveProject = () => projects.find((p) => p.id === activeProjectId) || null;
  activeProjectRef.current = getActiveProject();

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectForm.title.trim()) return;
    try {
      const res = await api.post('/sourcing/pre-rft/projects', newProjectForm);
      if (res.data.success) {
        const configProjects = res.data.config?.projects || [];
        setProjects(configProjects);
        setActiveProjectId(res.data.config?.activeProjectId || res.data.project.id);
        setShowNewProjectModal(false);
        setNewProjectForm({ title: '', description: '' });
        showToast('Project created successfully', 'success');
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create project', 'error');
    }
  };

  const handleDeleteProject = async (e, projectId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      const res = await api.delete(`/sourcing/pre-rft/projects/${projectId}`);
      if (res.data.success) {
        const configProjects = res.data.config?.projects || [];
        setProjects(configProjects);
        if (activeProjectId === projectId) {
          setActiveProjectId(configProjects.length > 0 ? configProjects[0].id : null);
          setExpandedPhase(null);
        }
        showToast('Project deleted', 'success');
      }
    } catch (err) {
      showToast('Failed to delete project', 'error');
    }
  };

  const saveProjectPhase = async (projectId, phaseId, data) => {
    setIsSaving(true);
    try {
      await api.put(`/sourcing/pre-rft/projects/${projectId}/phase/${phaseId}`, { data });
    } catch (err) {
      showToast('Auto-save failed', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (phaseId, key, value) => {
    setProjects((prev) =>
      prev.map((p) => {
        if (p.id === activeProjectId) {
          const updatedPhase = { ...(p[phaseId] || {}), [key]: value };
          return { ...p, [phaseId]: updatedPhase };
        }
        return p;
      })
    );

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      const updatedProj = activeProjectRef.current;
      if (updatedProj) {
        saveProjectPhase(activeProjectId, phaseId, updatedProj[phaseId]);
      }
    }, 1000);
  };

  const handleFillWithAI = async (phaseId) => {
    const proj = getActiveProject();
    if (!proj) return;

    const phaseDef = PHASES.find(p => p.id === phaseId);
    const currentData = activeProjectRef.current?.[phaseId] || {};
    const allFilled = phaseDef.fields.every((field) => {
      const val = currentData[field.key];
      return val !== null && val !== undefined && val !== '';
    });
    if (allFilled) {
      showToast(
        'All fields already filled from your DPR. Nothing to suggest.',
        'info'
      );
      return;
    }

    setFillingPhaseId(phaseId);
    try {
      const contextMsg = `Project Title: ${proj.title}\nDescription: ${proj.description}\n${proj.extractedText ? `Source Document Content:\n${proj.extractedText.substring(0, 8000)}` : ''}\n`;
      const prompt = `You are a municipal procurement expert specializing in complex infrastructure projects in India. 
      Your goal is to generate INCREDIBLY DETAILED, PROFESSIONAL, and COMPREHENSIVE content for the "${phaseDef.title}" phase of a major RFP.
      
      The source text provided is from a draft or similar project. Extract every possible detail.
      
      CRITICAL INSTRUCTIONS:
      1. CONTENT LENGTH: For each text/textarea field, provide at least 250-400 words of professional, technically sound content.
      2. DYNAMIC FIELDS: If you identify relevant information in the source document that doesn't fit the standard keys, ADD NEW KEYS to the JSON object. These keys will be dynamically added to our UI.
      3. TONE: Use formal, administrative language suitable for a 30+ page official municipal document.
      
      Context: ${contextMsg}
      
      Return a clean JSON object. Start with these REQUIRED keys: ${phaseDef.fields.map(f => f.key).join(', ')}
      Then add any OTHER relevant keys you discovered.
      
      Rules:
      - Use professional government procurement terminology (e.g., "Schedule of Rates", "Technical Sanction", "Administrative Sanction", "CDP Alignment").
      - If details are missing, use your expertise to synthesize realistic, high-quality professional content.
      - Return ONLY raw JSON text. No markdown fences.`;

      const aiRes = await api.post('/ai/gemini', { message: prompt });
      let aiText = aiRes.data?.response || '';
      
      let parsed = {};
      try {
        const jsonMatch = aiText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const rawParsed = JSON.parse(jsonMatch[0]);
          // STRICT FILTERING: Only accept keys that belong to this phase 
          // or are newly discovered dynamic keys (but not other phase IDs)
          const validKeys = phaseDef.fields.map(f => f.key);
          const otherPhaseIds = PHASES.map(p => p.id).filter(id => id !== phaseId);
          
          Object.keys(rawParsed).forEach(key => {
            // SURGICAL FILTER: Only allow keys for THIS phase
            if (validKeys.includes(key) || (!otherPhaseIds.includes(key) && !['_id', 'id'].includes(key))) {
              parsed[key] = rawParsed[key];
            }
          });
          console.log(`[AI:SurgicalExtraction] Data for Phase ${phaseId}:`, parsed);
        } else {
          throw new Error('No JSON found');
        }
      } catch (parseErr) {
        console.error('Failed to parse AI JSON:', aiText);
        parsed = phaseDef.fields.reduce((acc, field) => {
          if (field.type === 'checkbox') acc[field.key] = true;
          else if (field.type === 'number') acc[field.key] = 50000;
          else acc[field.key] = `AI Suggested ${field.label}`;
          return acc;
        }, {});
      }

      const currentPhaseData = activeProjectRef.current?.[phaseId] || {};

      const safeToApply = {};
      Object.entries(parsed).forEach(([key, value]) => {
        const existing = currentPhaseData[key];

        const existingIsEmpty = (
          existing === null ||
          existing === undefined ||
          existing === '' ||
          existing === false ||
          (Array.isArray(existing) && existing.length === 0)
        );

        const aiValueIsUseful = (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== false
        );

        if (existingIsEmpty && aiValueIsUseful) {
          safeToApply[key] = value;
        }
      });

      if (Object.keys(safeToApply).length === 0) {
        showToast(
          'All fields in this phase already have data from your DPR. Nothing to suggest.',
          'info'
        );
        setFillingPhaseId(null);
        return;
      }

      setProjects((prev) =>
        prev.map((p) => {
          if (p.id === activeProjectId) {
            return { ...p, [phaseId]: { ...currentPhaseData, ...safeToApply } };
          }
          return p;
        })
      );

      saveProjectPhase(activeProjectId, phaseId, { ...currentPhaseData, ...safeToApply });
      showToast(
        `Suggested ${Object.keys(safeToApply).length} missing field(s) for "${phaseDef.title}". Existing DPR data was preserved.`,
        'success'
      );
    } catch (err) {
      showToast('Failed to generate AI suggestions', 'error');
    } finally {
      setFillingPhaseId(null);
    }
  };

  const handleAddCustomField = (phaseId) => {
    const fieldName = window.prompt("Enter the name for the new field:");
    if (!fieldName) return;
    
    const key = fieldName.toLowerCase().replace(/\s+/g, '');
    handleFieldChange(phaseId, key, "");
  };

  const handleFillAllWithAI = async () => {
    if (!activeProjectId) return;
    if (!window.confirm('AI will suggest content for empty fields across all 10 phases. Fields already filled from your DPR will not be changed.')) return;
    
    // We'll use a local state or just handle it sequentially
    try {
      for (const phase of PHASES) {
        showToast(`Generating detailed content for ${phase.title}...`, 'info');
        await handleFillWithAI(phase.id);
      }
      showToast("All phases populated with incredible detail!", "success");
    } catch (err) {
      showToast("Failed to complete full AI generation", "error");
    }
  };

  const handleLoadSampleData = async () => {
    if (!activeProjectId) {
      showToast('Please select a project first', 'warning');
      return;
    }
    if (!window.confirm('This will overwrite all existing phase data with sample data. Continue?')) return;
    
    const transformedData = {};
    
    PHASES.forEach(phase => {
      const phaseSample = sampleData[phase.id];
      if (!phaseSample) return;
      
      const phaseData = { ...phaseSample };
      phase.fields.forEach(field => {
        if (field.type === 'list' && Array.isArray(phaseData[field.key])) {
          phaseData[field.key] = phaseData[field.key].join('\n');
        }
      });
      transformedData[phase.id] = phaseData;
    });
    
    setProjects(prev =>
      prev.map(p => {
        if (p.id === activeProjectId) {
          return { ...p, ...transformedData };
        }
        return p;
      })
    );
    
    for (const [phaseId, data] of Object.entries(transformedData)) {
      try {
        await saveProjectPhase(activeProjectId, phaseId, data);
      } catch (err) {
        console.error(`Failed to save phase ${phaseId}:`, err);
      }
    }
    
    showToast('Sample data loaded successfully!', 'success');
  };

  const handleDprUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type) && !file.name.endsWith('.pdf') && !file.name.endsWith('.docx')) {
      alert('Please upload a PDF or DOCX file.');
      return;
    }

    setDprUploading(true);
    setDprFileName('');

    const formData = new FormData();
    formData.append('file', file);

    console.log('[DPR Upload] Sending file:', {
      name: file.name,
      size: file.size,
      type: file.type,
    });
    console.log('[DPR Upload] FormData payload:');
    for (const [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`  ${key}:`, { name: value.name, size: value.size, type: value.type });
      } else {
        console.log(`  ${key}:`, value);
      }
    }

    try {
      const res = await api.post('/sourcing/pre-rft/parse-dpr', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });

      console.log('[DPR Upload] API response:', {
        status: res.status,
        body: res.data,
      });

      const responseFields = res.data?.fields || {};
      const responseLogs = res.data?.logs || {};
      const populatedKeys = Object.entries(responseFields)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key]) => key);
      const emptyKeys = Object.entries(responseFields)
        .filter(([, value]) => value === null || value === undefined || value === '')
        .map(([key]) => key);

      console.log('[DPR Upload] Fields populated:', populatedKeys);
      console.log('[DPR Upload] Fields empty/null:', emptyKeys);
      if (responseLogs.fieldsFound) {
        console.log('[DPR Upload] Backend fieldsFound:', responseLogs.fieldsFound);
      }
      if (responseLogs.fieldsMissed) {
        console.log('[DPR Upload] Backend fieldsMissed:', responseLogs.fieldsMissed);
      }

      if (res.data?.success && (res.data?.data || res.data?.fields)) {
        const aiData = res.data.data || {};
        const proj = getActiveProject();
        const phaseUpdates = {};

        Object.entries(aiData).forEach(([phaseId, phaseFields]) => {
          if (typeof phaseFields === 'object' && phaseFields !== null) {
            phaseUpdates[phaseId] = { ...(proj?.[phaseId] || {}), ...phaseFields };
          }
        });

        setProjects((prev) =>
          prev.map((p) => {
            if (p.id !== activeProjectId) return p;
            return { ...p, ...phaseUpdates };
          })
        );

        for (const [phaseId, phaseFields] of Object.entries(phaseUpdates)) {
          await saveProjectPhase(activeProjectId, phaseId, phaseFields);
        }

        setDprFileName(file.name);
        alert('✓ All forms auto-filled from DPR. Please review each phase before generating RFP.');
      } else {
        alert('AI could not extract data from the document. Please fill manually.');
      }
    } catch (err) {
      console.error('[DPR Upload] Error:', err);
      console.error('[DPR Upload] Error response:', {
        status: err?.response?.status,
        body: err?.response?.data,
      });
      const msg = err?.response?.data?.error || 'Failed to process DPR. Please try again.';
      alert(msg);
    } finally {
      setDprUploading(false);
      // Reset input so same file can be re-uploaded if needed
      if (dprInputRef.current) dprInputRef.current.value = '';
    }
  };

  const handleFileUpload = async (event, projectId) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);
    
    if (activeProjectId !== projectId) {
      await api.put('/sourcing/pre-rft/active', { projectId }).catch(() => {});
      setActiveProjectId(projectId);
    }

    try {
      const res = await api.post('/sourcing/pre-rft/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        showToast(`${file.name} uploaded and processed!`, 'success');
        loadData();
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to upload document', 'error');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const generateRftDocument = async () => {
    if (!activeProjectId) return;
    
    // Check if we need to fill phases first
    const completed = getPhaseProgress(activeProject);
    if (completed < 10) {
      const fillAll = window.confirm(`Only ${completed}/10 phases are started. Would you like to use AI to generate incredibly detailed content for all phases before creating the final document?`);
      if (fillAll) {
        await handleFillAllWithAI();
      }
    }

    setIsGeneratingDocument(true);
    try {
      const res = await api.post(`/sourcing/pre-rft/projects/${activeProjectId}/generate-document`);
      if (res.data.success) {
        setGeneratedDocument(res.data.document);
        setShowDocumentModal(true);
        showToast('RFT Document generated successfully', 'success');
      }
    } catch (err) {
      showToast('Failed to generate RFT document', 'error');
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const getPhaseProgress = (project) => {
    if (!project) return 0;
    let completed = 0;
    PHASES.forEach(phase => {
      const phaseData = project[phase.id] || {};
      const filledFields = Object.keys(phaseData).filter(k => phaseData[k] !== '' && phaseData[k] !== null).length;
      if (filledFields > 0) completed++;
    });
    return completed;
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-[#F6FAFF]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const activeProject = getActiveProject();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
  // Derive server base url by replacing /api/v1
  const serverBaseUrl = apiBaseUrl.replace('/api/v1', '');

  return (
    <div className="flex flex-col h-full bg-[#F6FAFF] font-sans">
      

      <div className="flex-1 px-6 pb-6 mt-4 overflow-y-auto">

        {/* Right Panel */}
        <div className="w-full flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
          {!activeProject ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <FileText size={48} className="mb-4 text-gray-300" />
              <p>Select a project to view its workflow phases</p>
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-3">
  <button
    onClick={() => navigate('/sourcing/pre-rft')}
    className="px-3 py-1 border rounded-lg hover:bg-gray-100"
  >
    ←
  </button>

  <h2 className="text-xl font-bold text-gray-900">
    {activeProject.title}
  </h2>
</div>
                    {activeProject.documentUrl && (
                      <a href={`${serverBaseUrl}${activeProject.documentUrl}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded border border-blue-200 hover:bg-blue-100 transition-colors font-medium">
                        <ExternalLink size={12} /> View Source Document
                      </a>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">{activeProject.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    {isSaving ? <><Clock size={14} className="animate-spin" /> Saving...</> : <><CheckCircle size={14} className="text-green-500" /> Saved</>}
                  </span>
                  <div className="flex flex-col items-start">
                    {/* Hidden file input */}
                    <input
                      ref={dprInputRef}
                      type="file"
                      accept=".pdf,.docx"
                      style={{ display: 'none' }}
                      onChange={handleDprUpload}
                    />

                    {/* Upload button */}
                    <button
                      onClick={() => dprInputRef.current && dprInputRef.current.click()}
                      disabled={dprUploading}
                      className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 shadow-sm"
                      title="Upload DPR document"
                    >
                      <FileText size={16} />
                      {dprUploading ? 'Analyzing DPR...' : 'Upload DPR Document'}
                    </button>

                    {/* Filename confirmation */}
                    {dprFileName && (
                      <div style={{ fontSize: '12px', color: '#4ade80', marginTop: '4px' }}>
                        ✓ {dprFileName} — forms filled
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleLoadSampleData}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 shadow-sm"
                    title="Load sample data into all phases"
                  >
                    <FileText size={16} />
                    Load Sample Data
                  </button>
                  <button 
                    onClick={generateRftDocument}
                    disabled={isGeneratingDocument}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg hover:from-black hover:to-gray-900 shadow-sm"
                  >
                    {isGeneratingDocument ? <Clock size={16} className="animate-spin" /> : <Wand2 size={16} />}
                    Generate RFT Document
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 relative">
                {isUploading && !showDocumentModal && (
                  <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center">
                    <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4" />
                    <h3 className="text-lg font-bold text-gray-900">Processing Document</h3>
                    <p className="text-gray-500 mt-2">Extracting text and mapping to workflow phases using AI...</p>
                  </div>
                )}
                 {/* Phase Navigation */}
  <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
    <div className="flex items-center justify-between">

      {PHASES.map((phase, index) => (
        <div
  key={phase.id}
  onClick={() => setActivePhase(phase.id)}
  className="flex flex-col items-center cursor-pointer flex-1"
>
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all ${
              activePhase === phase.id
                ? "bg-indigo-500 text-white border-indigo-500"
                : "bg-white text-gray-500 border-indigo-300"
            }`}
          >
            {index + 1}
          </div>

          <div className="mt-2 h-14 flex items-start justify-center">
  <span className="text-xs text-center font-medium leading-4">
    {phase.title}
  </span>
</div>
        </div>
      ))}

    </div>
  </div>

  <div className="space-y-4">

  </div>
                {PHASES
  .filter(phase => phase.id === activePhase)
  .map((phase, index) => {
                  const isExpanded = true;
                  const phaseData = activeProject[phase.id] || {};
                  const isCompleted = Object.keys(phaseData).length > 0;

                  return (
                    <div id={`phase-${phase.id}`} key={phase.id} className={`rounded-xl border transition-all duration-300 overflow-hidden ${isExpanded ? 'border-indigo-200 shadow-md ring-1 ring-indigo-100' : 'border-gray-200 hover:border-indigo-200'}`}>
                      <div className={`w-full p-4 flex items-center justify-between transition-colors ${isExpanded ? 'bg-indigo-50/50' : 'bg-white hover:bg-gray-50'}`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${phase.color}`}>{phase.icon}</div>
                          <div className="text-left">
                            <h3 className="font-semibold text-gray-900">
  <span className="text-gray-400 mr-2 text-sm">
    Phase {PHASES.findIndex(p => p.id === phase.id) + 1}
  </span>
  {phase.title}
</h3>
                            <p className="text-xs text-gray-500 mt-0.5">{phase.subtitle}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {isCompleted && !isExpanded && <CheckCircle size={18} className="text-green-500" />}
                          {isExpanded ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                        </div>
                      </div>

                      { (
                        <div className="p-6 bg-white border-t border-gray-100 relative">
                          {fillingPhaseId === phase.id && (
                            <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-[20] flex flex-col items-center justify-center animate-in fade-in duration-300">
                              <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-3 shadow-sm"></div>
                              <p className="text-sm font-bold text-indigo-900 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">Suggesting missing fields...</p>
                            </div>
                          )}

                          {workflowMode === 'manual' && (
                            <div className="flex justify-between items-center mb-6">
                              <h4 className={`font-medium ${phase.headerColor}`}>Fill details below or use AI</h4>
                              <button
                                onClick={() => handleFillWithAI(phase.id)}
                                disabled={fillingPhaseId !== null}
                                className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-all ${
                                  fillingPhaseId === phase.id
                                    ? 'bg-indigo-50 text-indigo-400 cursor-not-allowed'
                                    : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                }`}
                              >
                                <Wand2 size={14} className={fillingPhaseId === phase.id ? 'animate-pulse' : ''} />
                                {fillingPhaseId === phase.id ? 'Generating...' : 'Suggest Missing Fields'}
                              </button>
                            </div>
                          )}

                          {workflowMode === 'automatic' && (
                            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 flex items-start gap-2">
                              <Settings2 size={16} className="mt-0.5 flex-shrink-0" />
                              <p><strong>Automatic Mode:</strong> Upload a PDF to automatically extract and map data into all fields using AI. You can still manually edit below.</p>
                            </div>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {phase.fields.map(field => (
                              <div key={field.key} className={field.type === 'textarea' ? 'col-span-full' : ''}>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">{field.label}</label>
                                {field.type === 'textarea' ? (
                                  <textarea
                                    value={phaseData[field.key] || ''}
                                    onChange={(e) => handleFieldChange(phase.id, field.key, e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 min-h-[150px] text-sm leading-relaxed"
                                  />
                                ) : field.type === 'checkbox' ? (
                                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                    <input type="checkbox" checked={phaseData[field.key] || false} onChange={(e) => handleFieldChange(phase.id, field.key, e.target.checked)} className="w-4 h-4 text-indigo-600 rounded border-gray-300" />
                                    <span className="text-sm text-gray-700">{field.label}</span>
                                  </label>
                                ) : field.type === 'list' ? (
                                  <div>
                                    <textarea
                                      value={Array.isArray(phaseData[field.key]) ? phaseData[field.key].join('\n') : (phaseData[field.key] || '')}
                                      onChange={(e) => handleFieldChange(phase.id, field.key, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px] text-sm leading-relaxed"
                                      placeholder="Enter one item per line"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Enter one item per line</p>
                                  </div>
                                ) : (
                                  <input
                                    type={field.type === 'number' ? 'number' : 'text'}
                                    value={phaseData[field.key] || ''}
                                    onChange={(e) => handleFieldChange(phase.id, field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                  />
                                )}
                              </div>
                            ))}

                            {/* DYNAMIC FIELDS: Render keys present in data but not in PHASES definition */}
                            {Object.entries(phaseData)
                              .filter(([key]) => !phase.fields.some(f => f.key === key) && !['_id', 'id', 'createdAt', 'updatedAt'].includes(key))
                              .map(([key, value]) => (
                                <div key={key} className="col-span-full relative group">
                                  <div className="flex justify-between items-center mb-1.5">
                                    <label className="block text-sm font-bold text-indigo-800 uppercase tracking-tight">
                                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} (AI Added)
                                    </label>
                                    <button 
                                      onClick={() => {
                                        const { [key]: deleted, ...rest } = phaseData;
                                        handleFieldChange(phase.id, '_dummy', ''); // trigger save
                                        setProjects(prev => prev.map(p => p.id === activeProjectId ? { ...p, [phase.id]: rest } : p));
                                      }}
                                      className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                  <textarea
                                    value={value || ''}
                                    onChange={(e) => handleFieldChange(phase.id, key, e.target.value)}
                                    className="w-full px-3 py-2 border border-indigo-200 bg-indigo-50/10 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 min-h-[150px] text-sm leading-relaxed"
                                  />
                                </div>
                              ))
                            }

                            {/* Add Field Button */}
                            <div className="col-span-full border-t border-dashed border-gray-200 pt-4 flex justify-center">
                              <button 
                                onClick={() => handleAddCustomField(phase.id)}
                                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 transition-colors"
                              >
                                <Plus size={14} /> Add Custom Detailed Section
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

        </div>
      </div>

      {showNewProjectModal &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            style={{ zIndex: 1100 }}
            onClick={() => setShowNewProjectModal(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-project-modal-title"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                <h2 id="new-project-modal-title" className="text-lg font-bold text-gray-900">Create New Project</h2>
                <button type="button" onClick={() => setShowNewProjectModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
              </div>
              <form onSubmit={handleCreateProject} className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Project Title</label>
                    <input type="text" required value={newProjectForm.title} onChange={e => setNewProjectForm({...newProjectForm, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea value={newProjectForm.description} onChange={e => setNewProjectForm({...newProjectForm, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-24" />
                  </div>
                </div>
                <div className="mt-8 flex gap-3 justify-end">
                  <button type="button" onClick={() => setShowNewProjectModal(false)} className="px-4 py-2 text-gray-700 font-medium hover:bg-gray-50 rounded-lg">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700">Create</button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )}

      {showDocumentModal &&
        generatedDocument &&
        createPortal(
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            style={{ zIndex: 1100 }}
            onClick={() => setShowDocumentModal(false)}
            role="presentation"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-full max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="rft-document-modal-title"
            >
              <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 text-blue-700 rounded-lg">
                    <FileText size={20} />
                  </div>
                  <div>
                    <h2 id="rft-document-modal-title" className="text-lg font-bold text-gray-900">Generated RFT Document</h2>
                    <p className="text-xs text-gray-500">Review the AI-generated Request for Proposal based on your phase data.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      const blob = new Blob([generatedDocument], { type: 'text/markdown' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `RFT_${activeProject?.title.replace(/\s+/g, '_')}.md`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
                  >
                    <Download size={16} />
                    Download
                  </button>
                  <button type="button" onClick={() => setShowDocumentModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-8 bg-gray-50">
                <div className="max-w-4xl mx-auto bg-white p-10 rounded-xl shadow-sm border border-gray-200 prose prose-indigo max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-gray-800 text-sm leading-relaxed" style={{ fontFamily: 'inherit' }}>
                    {generatedDocument}
                  </pre>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

